import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiPlus, FiEdit2, FiTrash2, FiMove, FiSave, FiX, FiCheck } from 'react-icons/fi';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

const Container = styled.div`
  padding: 30px;
  background: #f8f9fa;
  min-height: calc(100vh - 80px);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  h1 {
    font-size: 28px;
    color: #333;
    margin: 0;
  }
`;

const AddButton = styled.button`
  padding: 12px 24px;
  background: #3498db;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:hover {
    background: #2980b9;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  }
`;

const QuestionsList = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const QuestionItem = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
  background: ${props => props.isDragging ? '#f0f7ff' : 'white'};
  cursor: ${props => props.isEditing ? 'default' : 'move'};
  transition: all 0.2s;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background: #f8f9fa;
  }
  
  .question-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }
  
  .question-order {
    background: #3498db;
    color: white;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 14px;
  }
  
  .question-text {
    flex: 1;
    margin: 0 15px;
    font-size: 16px;
    font-weight: 500;
    color: #333;
  }
  
  .question-actions {
    display: flex;
    gap: 10px;
  }
  
  .question-meta {
    display: flex;
    gap: 15px;
    font-size: 13px;
    color: #666;
    margin-top: 10px;
  }
  
  .badge {
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 600;
  }
  
  .badge.text {
    background: #e3f2fd;
    color: #1976d2;
  }
  
  .badge.dropdown {
    background: #f3e5f5;
    color: #7b1fa2;
  }
  
  .badge.info {
    background: #fff3e0;
    color: #f57c00;
  }
  
  .badge.enabled {
    background: #e8f5e9;
    color: #2e7d32;
  }
  
  .badge.disabled {
    background: #ffebee;
    color: #c62828;
  }
`;

const IconButton = styled.button`
  padding: 8px;
  background: ${props => props.danger ? '#e74c3c' : '#f0f0f0'};
  color: ${props => props.danger ? 'white' : '#666'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.danger ? '#c0392b' : '#e0e0e0'};
    transform: scale(1.05);
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: ${props => props.show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 30px;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  
  h2 {
    margin: 0 0 20px 0;
    color: #333;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  
  label {
    display: block;
    margin-bottom: 8px;
    font-weight: 600;
    color: #333;
  }
  
  input, textarea, select {
    width: 100%;
    padding: 12px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    font-size: 14px;
    font-family: inherit;
    
    &:focus {
      outline: none;
      border-color: #3498db;
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 80px;
  }
`;

const ValidationsList = styled.div`
  margin-top: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  background: #f8f9fa;
`;

const ValidationItem = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  align-items: flex-start;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  select, input {
    flex: 1;
    padding: 8px;
    border: 1px solid #e0e0e0;
    border-radius: 6px;
    font-size: 13px;
  }
`;

const AddValidationButton = styled.button`
  padding: 8px 16px;
  background: #2ecc71;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  margin-top: 10px;
  
  &:hover {
    background: #27ae60;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 30px;
`;

const Button = styled.button`
  padding: 12px 24px;
  background: ${props => props.primary ? '#3498db' : '#e0e0e0'};
  color: ${props => props.primary ? 'white' : '#666'};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  
  &:hover {
    background: ${props => props.primary ? '#2980b9' : '#d0d0d0'};
  }
`;

const ChatQuestionsManagement = () => {
  const [questions, setQuestions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    questionType: 'text',
    stepIdentifier: '',
    options: [],
    validations: [],
    enabled: true,
    saveToField: '',
    fieldType: 'existing',
    customFieldName: '',
    addToLeads: false
  });

  useEffect(() => {
    fetchQuestions();
  }, []);

  const fetchQuestions = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(
        `${API_URL}/api/admin/chat/questions`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        setQuestions(response.data.questions);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      alert('Failed to fetch questions');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const items = Array.from(questions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update order values
    const updatedQuestions = items.map((q, index) => ({
      ...q,
      order: index + 1
    }));
    
    setQuestions(updatedQuestions);
    
    // Save new order to backend
    try {
      const token = localStorage.getItem('adminToken');
      await axios.post(
        `${API_URL}/api/admin/chat/questions/reorder`,
        { questions: updatedQuestions.map(q => ({ id: q._id, order: q.order })) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (error) {
      console.error('Error reordering questions:', error);
      alert('Failed to save new order');
      fetchQuestions(); // Revert on error
    }
  };

  const openAddModal = () => {
    setEditingQuestion(null);
    setFormData({
      question: '',
      questionType: 'text',
      stepIdentifier: '',
      options: [],
      validations: [],
      enabled: true,
      saveToField: '',
      fieldType: 'existing',
      customFieldName: '',
      addToLeads: false
    });
    setShowModal(true);
  };

  const openEditModal = (question) => {
    setEditingQuestion(question);
    setFormData({
      question: question.question,
      questionType: question.questionType,
      stepIdentifier: question.stepIdentifier,
      options: question.options || [],
      validations: question.validations || [],
      enabled: question.enabled,
      saveToField: question.saveToField || '',
      fieldType: question.fieldType || 'existing',
      customFieldName: question.customFieldName || '',
      addToLeads: question.addToLeads || false
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      if (editingQuestion) {
        // Update existing
        await axios.put(
          `${API_URL}/api/admin/chat/questions/${editingQuestion._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Create new
        await axios.post(
          `${API_URL}/api/admin/chat/questions`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setShowModal(false);
      fetchQuestions();
    } catch (error) {
      console.error('Error saving question:', error);
      alert('Failed to save question');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this question?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(
        `${API_URL}/api/admin/chat/questions/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      fetchQuestions();
    } catch (error) {
      console.error('Error deleting question:', error);
      alert('Failed to delete question');
    }
  };

  const addValidation = () => {
    setFormData({
      ...formData,
      validations: [
        ...formData.validations,
        { type: 'email', value: '', errorMessage: 'Invalid input' }
      ]
    });
  };

  const updateValidation = (index, field, value) => {
    const newValidations = [...formData.validations];
    newValidations[index][field] = value;
    setFormData({ ...formData, validations: newValidations });
  };

  const removeValidation = (index) => {
    const newValidations = formData.validations.filter((_, i) => i !== index);
    setFormData({ ...formData, validations: newValidations });
  };

  return (
    <Container>
      <Header>
        <h1>Chat Questions Management</h1>
        <AddButton onClick={openAddModal}>
          <FiPlus />
          Add Question
        </AddButton>
      </Header>

      <QuestionsList>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="questions">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {questions.map((question, index) => (
                  <Draggable
                    key={question._id}
                    draggableId={question._id}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <QuestionItem
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        isDragging={snapshot.isDragging}
                      >
                        <div className="question-header">
                          <div className="question-order">{index + 1}</div>
                          <div className="question-text">{question.question}</div>
                          <div className="question-actions">
                            <IconButton onClick={() => openEditModal(question)}>
                              <FiEdit2 />
                            </IconButton>
                            <IconButton danger onClick={() => handleDelete(question._id)}>
                              <FiTrash2 />
                            </IconButton>
                          </div>
                        </div>
                        <div className="question-meta">
                          <span className={`badge ${question.questionType}`}>
                            {question.questionType}
                          </span>
                          <span className={`badge ${question.enabled ? 'enabled' : 'disabled'}`}>
                            {question.enabled ? 'Enabled' : 'Disabled'}
                          </span>
                          {question.validations && question.validations.length > 0 && (
                            <span className="badge">{question.validations.length} validations</span>
                          )}
                          {question.addToLeads && (
                            <span className="badge enabled">→ Leads</span>
                          )}
                        </div>
                      </QuestionItem>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </QuestionsList>

      <Modal show={showModal}>
        <ModalContent>
          <h2>{editingQuestion ? 'Edit Question' : 'Add New Question'}</h2>
          
          <FormGroup>
            <label>Question Text *</label>
            <textarea
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              placeholder="What would you like to ask?"
            />
          </FormGroup>

          <FormGroup>
            <label>Question Type *</label>
            <select
              value={formData.questionType}
              onChange={(e) => setFormData({ ...formData, questionType: e.target.value })}
            >
              <option value="text">Text Input</option>
              <option value="dropdown">Dropdown</option>
              <option value="info">Information Only</option>
            </select>
          </FormGroup>

          <FormGroup>
            <label>Step Identifier *</label>
            <input
              type="text"
              value={formData.stepIdentifier}
              onChange={(e) => setFormData({ ...formData, stepIdentifier: e.target.value })}
              placeholder="e.g., customer_name, email, phone"
            />
          </FormGroup>

          <FormGroup>
            <label>
              <input
                type="checkbox"
                checked={formData.enabled}
                onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                style={{ width: 'auto', marginRight: '8px' }}
              />
              Enabled
            </label>
          </FormGroup>

          <FormGroup>
            <label>
              <input
                type="checkbox"
                checked={formData.addToLeads}
                onChange={(e) => setFormData({ ...formData, addToLeads: e.target.checked })}
                style={{ width: 'auto', marginRight: '8px' }}
              />
              Add to Leads Database
            </label>
          </FormGroup>

          <FormGroup>
            <label>Validations</label>
            <ValidationsList>
              {formData.validations.map((validation, index) => (
                <ValidationItem key={index}>
                  <select
                    value={validation.type}
                    onChange={(e) => updateValidation(index, 'type', e.target.value)}
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="minLength">Min Length</option>
                    <option value="maxLength">Max Length</option>
                    <option value="number">Number</option>
                    <option value="url">URL</option>
                    <option value="regex">Regex</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Value (if needed)"
                    value={validation.value}
                    onChange={(e) => updateValidation(index, 'value', e.target.value)}
                  />
                  <input
                    type="text"
                    placeholder="Error message"
                    value={validation.errorMessage}
                    onChange={(e) => updateValidation(index, 'errorMessage', e.target.value)}
                  />
                  <IconButton danger onClick={() => removeValidation(index)}>
                    <FiX />
                  </IconButton>
                </ValidationItem>
              ))}
              <AddValidationButton onClick={addValidation}>
                <FiPlus /> Add Validation
              </AddValidationButton>
            </ValidationsList>
          </FormGroup>

          <ButtonGroup>
            <Button onClick={() => setShowModal(false)}>
              <FiX />
              Cancel
            </Button>
            <Button primary onClick={handleSave}>
              <FiSave />
              Save Question
            </Button>
          </ButtonGroup>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default ChatQuestionsManagement;
